import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, Send, X, ChevronLeft, Plus, Search, Megaphone, Paperclip, FileText, Check } from "lucide-react";
import { toast } from "sonner";

const CH_LABEL = { committee: "Comité des parents", coach: "Coachs", admin: "Club / Admin" };
const INBOX_FILTERS = [
  { key: "all", label: "Tous" },
  { key: "admin", label: "Club" },
  { key: "coach", label: "Coachs" },
  { key: "committee", label: "Comité" },
];

function Attachment({ m }) {
  if (!m.file_path) return null;
  const url = fileUrl(m.file_path);
  const isImg = (m.file_type || "").startsWith("image/");
  if (isImg) {
    return (
      <a href={url} target="_blank" rel="noreferrer" data-testid="message-attachment">
        <img src={url} alt={m.file_name || "image"} className="mt-1.5 rounded-lg max-h-44 max-w-full object-cover border border-black/10" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="message-attachment"
      className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg bg-black/10 px-2.5 py-1.5 text-xs font-semibold underline">
      <FileText size={13} /> {m.file_name || "Document PDF"}
    </a>
  );
}

export default function MessageBubble() {
  const { user, hasRole, committee } = useAuth();
  const isStaff = hasRole?.("admin") || hasRole?.("coach") || committee;
  const isAdmin = hasRole?.("admin");
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // staff: {uid, channel}
  const [composing, setComposing] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [myChannels, setMyChannels] = useState([]);
  const [composeChannel, setComposeChannel] = useState("admin");
  const [contactSearch, setContactSearch] = useState("");
  const [inboxFilter, setInboxFilter] = useState("all");
  // Diffusion
  const [broadcasting, setBroadcasting] = useState(false);
  const [audience, setAudience] = useState([]);
  const [audFilters, setAudFilters] = useState([]);
  const [selIds, setSelIds] = useState(new Set());
  const [bcText, setBcText] = useState("");
  const [bcSearch, setBcSearch] = useState("");
  const [sending, setSending] = useState(false);
  // Pièce jointe (composer conversation) + diffusion
  const [attach, setAttach] = useState(null);
  const [bcAttach, setBcAttach] = useState(null);
  const [uploading, setUploading] = useState(false);
  const endRef = useRef(null);

  const fetchUnread = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  useEffect(() => {
    if (!user) return;
    fetchUnread();
    const t = setInterval(fetchUnread, 20000);
    return () => clearInterval(t);
  }, [user]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const loadMember = () => api.get("/messages/mine?channel=all").then((r) => { setMessages(r.data); fetchUnread(); });
  const loadThreads = () => api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => setThreads([]));
  const loadThread = (uid, ch) => { setActive({ uid, channel: ch }); setComposing(false); setBroadcasting(false); api.get(`/messages/thread/${uid}?channel=${ch}`).then((r) => { setMessages(r.data); fetchUnread(); }); };

  const openPanel = () => {
    setOpen(true); setComposing(false); setBroadcasting(false); setActive(null);
    if (isStaff) loadThreads(); else loadMember();
  };

  const openCompose = async () => {
    try {
      const { data } = await api.get("/messages/contacts");
      setContacts(data.contacts || []);
      setMyChannels(data.channels || []);
      setComposeChannel((data.channels || []).includes("admin") ? "admin" : (data.channels || [])[0] || "admin");
      setContactSearch(""); setComposing(true); setActive(null);
    } catch { setContacts([]); }
  };
  const startThread = (contact) => { setComposing(false); loadThread(contact.user_id, composeChannel); };

  const openBroadcast = async () => {
    try {
      const { data } = await api.get("/messages/audience");
      setAudience(data.recipients || []);
      setAudFilters(data.filters || []);
      setSelIds(new Set()); setBcText(""); setBcSearch(""); setBcAttach(null);
      setBroadcasting(true); setActive(null); setComposing(false);
    } catch { toast.error("Chargement impossible"); }
  };

  const matchFilter = (r, key) => {
    if (key === "gr1" || key === "gr2") return (r.groups || []).includes(key);
    if (key === "comite") return (r.functions || []).includes("comite_parents");
    return (r.functions || []).includes(key); // coach / benevole
  };
  const toggleFilter = (key) => {
    const ids = audience.filter((r) => matchFilter(r, key)).map((r) => r.user_id);
    if (!ids.length) { toast.info("Aucun destinataire pour ce filtre"); return; }
    setSelIds((prev) => {
      const next = new Set(prev);
      const allIn = ids.every((id) => next.has(id));
      ids.forEach((id) => (allIn ? next.delete(id) : next.add(id)));
      return next;
    });
  };
  const toggleAll = () => {
    setSelIds((prev) => (prev.size === audience.length ? new Set() : new Set(audience.map((r) => r.user_id))));
  };
  const toggleOne = (id) => setSelIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const doUpload = async (file, setter) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/messages/attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter({ path: data.path, name: data.name, type: data.type });
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi du fichier échoué"); }
    finally { setUploading(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t && !attach) return;
    const payload = { text: t };
    if (attach) { payload.file_path = attach.path; payload.file_name = attach.name; payload.file_type = attach.type; }
    setText(""); setAttach(null);
    if (isStaff && active) {
      await api.post(`/messages/thread/${active.uid}/reply`, { ...payload, channel: active.channel });
      loadThread(active.uid, active.channel); loadThreads();
    } else {
      await api.post("/messages", { ...payload, channel: "admin" });
      loadMember();
    }
  };

  const sendBroadcast = async () => {
    if (!bcText.trim() && !bcAttach) { toast.error("Écrivez un message ou joignez un fichier."); return; }
    if (!selIds.size) { toast.error("Sélectionnez au moins un destinataire."); return; }
    setSending(true);
    try {
      const payload = { text: bcText.trim(), user_ids: Array.from(selIds), title: "Message du club" };
      if (bcAttach) { payload.file_path = bcAttach.path; payload.file_name = bcAttach.name; payload.file_type = bcAttach.type; }
      const { data } = await api.post("/messages/broadcast", payload);
      toast.success(`Message envoyé à ${data.sent} destinataire(s)`);
      setBroadcasting(false); loadThreads();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    finally { setSending(false); }
  };

  if (!user) return null;

  const shownContacts = contacts.filter((c) => (c.name || "").toLowerCase().includes(contactSearch.toLowerCase()));
  const shownAudience = audience.filter((r) => (r.name || "").toLowerCase().includes(bcSearch.toLowerCase()));
  const shownThreads = threads.filter((t) => inboxFilter === "all" || t.channel === inboxFilter);

  const headerTitle = () => {
    if (!isStaff) return "Contacter le club";
    if (active) return `${threads.find((t) => t.user_id === active.uid && t.channel === active.channel)?.user_name || "Conversation"} · ${CH_LABEL[active.channel]}`;
    if (broadcasting) return "Diffusion à plusieurs";
    if (composing) return "Nouveau message";
    return "Boîte de réception";
  };

  return (
    <>
      <button data-testid="message-bubble-btn" onClick={() => (open ? setOpen(false) : openPanel())}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X size={24} /> : <MessageCircle size={24} />}
        {!open && unread > 0 && (
          <span data-testid="message-unread-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">{unread}</span>
        )}
      </button>

      {open && (
        <div data-testid="message-panel" className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[32rem] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden">
          <div className="px-4 py-3 bg-secondary/60 border-b border-border flex items-center gap-2">
            {isStaff && (active || composing || broadcasting) && (
              <button data-testid="panel-back-btn" onClick={() => { setActive(null); setComposing(false); setBroadcasting(false); loadThreads(); }} className="text-muted-foreground hover:text-foreground"><ChevronLeft size={18} /></button>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-bold text-sm truncate">{headerTitle()}</p>
              <p className="text-xs text-muted-foreground truncate">
                {!isStaff ? "Écrivez au club, il vous répond ici." : active ? "" : broadcasting ? "Choisissez les destinataires" : composing ? "Choisissez un destinataire" : "Vos conversations"}
              </p>
            </div>
            {isStaff && !active && !composing && !broadcasting && (
              <div className="flex items-center gap-1.5 shrink-0">
                {isAdmin && (
                  <button data-testid="broadcast-btn" onClick={openBroadcast} title="Diffusion à plusieurs"
                    className="h-8 px-2.5 rounded-lg bg-secondary text-foreground text-xs font-bold flex items-center gap-1 hover:bg-secondary/70 transition-colors">
                    <Megaphone size={14} /> Diffusion
                  </button>
                )}
                <button data-testid="new-message-btn" onClick={openCompose}
                  className="h-8 px-2.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold flex items-center gap-1 hover:opacity-90 transition-opacity">
                  <Plus size={14} /> Nouveau
                </button>
              </div>
            )}
          </div>

          {/* Diffusion (admin) */}
          {isStaff && broadcasting && (
            <div className="flex-1 flex flex-col overflow-hidden" data-testid="broadcast-view">
              <div className="p-2 border-b border-border">
                <div className="flex gap-1.5 flex-wrap">
                  <button data-testid="broadcast-filter-all" onClick={toggleAll}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors ${selIds.size === audience.length && audience.length ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>Tous</button>
                  {audFilters.map((f) => (
                    <button key={f.key} data-testid={`broadcast-filter-${f.key}`} onClick={() => toggleFilter(f.key)}
                      className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-secondary text-muted-foreground hover:text-foreground transition-colors">{f.label}</button>
                  ))}
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-secondary border border-border px-3 mt-2">
                  <Search size={14} className="text-muted-foreground shrink-0" />
                  <input data-testid="broadcast-search" value={bcSearch} onChange={(e) => setBcSearch(e.target.value)}
                    placeholder="Rechercher une personne..." className="flex-1 bg-transparent py-2 text-sm outline-none" />
                </div>
                <p data-testid="broadcast-selected-count" className="text-[11px] text-muted-foreground mt-1.5 font-semibold">{selIds.size} destinataire(s) sélectionné(s)</p>
              </div>
              <div className="flex-1 overflow-y-auto">
                {shownAudience.map((r) => {
                  const sel = selIds.has(r.user_id);
                  const tags = [...(r.groups || []).map((g) => g.toUpperCase()), ...(r.functions || []).map((f) => ({ coach: "Coach", comite_parents: "Comité", benevole: "Bénévole" }[f] || f))];
                  return (
                    <button key={r.user_id} data-testid={`broadcast-recipient-${r.user_id}`} onClick={() => toggleOne(r.user_id)}
                      className="w-full text-left px-4 py-2.5 border-b border-border hover:bg-secondary/40 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{r.name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{tags.length ? tags.join(" · ") : (r.email || "")}</p>
                      </div>
                      <span className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center ${sel ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>{sel && <Check size={13} />}</span>
                    </button>
                  );
                })}
                {!shownAudience.length && <p className="p-4 text-sm text-muted-foreground">Aucun destinataire.</p>}
              </div>
              <div className="p-2 border-t border-border space-y-2">
                {bcAttach && (
                  <div className="flex items-center justify-between gap-2 rounded-lg bg-secondary px-2.5 py-1.5 text-xs">
                    <span className="truncate flex items-center gap-1"><Paperclip size={12} /> {bcAttach.name}</span>
                    <button onClick={() => setBcAttach(null)} className="text-muted-foreground hover:text-destructive"><X size={14} /></button>
                  </div>
                )}
                <textarea data-testid="broadcast-text" value={bcText} onChange={(e) => setBcText(e.target.value)} rows={2}
                  placeholder="Votre message à diffuser..." className="w-full rounded-lg bg-secondary border border-border px-3 py-2 text-sm outline-none focus:border-primary resize-none" />
                <div className="flex items-center gap-2">
                  <label data-testid="broadcast-attach-label" className="shrink-0 w-9 h-9 rounded-lg bg-secondary border border-border flex items-center justify-center cursor-pointer hover:bg-secondary/70">
                    <Paperclip size={16} />
                    <input data-testid="broadcast-attach-input" type="file" accept="image/*,application/pdf" className="hidden"
                      onChange={(e) => { doUpload(e.target.files?.[0], setBcAttach); e.target.value = ""; }} />
                  </label>
                  <button data-testid="broadcast-send-btn" onClick={sendBroadcast} disabled={sending || uploading}
                    className="flex-1 h-9 rounded-lg bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
                    <Send size={15} /> {sending ? "Envoi..." : uploading ? "Chargement..." : "Diffuser"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Composer (staff : démarrer une conversation) */}
          {isStaff && composing && (
            <div className="flex-1 flex flex-col overflow-hidden" data-testid="compose-view">
              {myChannels.length > 1 && (
                <div className="flex gap-1 p-2 border-b border-border bg-secondary/30">
                  {myChannels.map((c) => (
                    <button key={c} data-testid={`compose-channel-${c}`} onClick={() => setComposeChannel(c)}
                      className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${composeChannel === c ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                      {CH_LABEL[c]}
                    </button>
                  ))}
                </div>
              )}
              <div className="p-2 border-b border-border">
                <div className="flex items-center gap-2 rounded-lg bg-secondary border border-border px-3">
                  <Search size={14} className="text-muted-foreground shrink-0" />
                  <input data-testid="contact-search" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)}
                    placeholder="Rechercher un parent / joueur..." className="flex-1 bg-transparent py-2 text-sm outline-none" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {shownContacts.map((c) => (
                  <button key={c.user_id} data-testid={`contact-${c.user_id}`} onClick={() => startThread(c)}
                    className="w-full text-left px-4 py-3 border-b border-border hover:bg-secondary/40 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{c.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{c.role === "player" ? "Joueur" : "Parent"}{c.email ? ` · ${c.email}` : ""}</p>
                    </div>
                  </button>
                ))}
                {!shownContacts.length && <p className="p-4 text-sm text-muted-foreground">Aucun contact disponible.</p>}
              </div>
            </div>
          )}

          {/* Inbox filtre (staff) */}
          {isStaff && !active && !composing && !broadcasting && isAdmin && (
            <div className="flex gap-1 p-2 border-b border-border bg-secondary/30">
              {INBOX_FILTERS.map((f) => (
                <button key={f.key} data-testid={`inbox-filter-${f.key}`} onClick={() => setInboxFilter(f.key)}
                  className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${inboxFilter === f.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                  {f.label}
                </button>
              ))}
            </div>
          )}

          {!composing && !broadcasting && (isStaff && !active ? (
            <div className="flex-1 overflow-y-auto">
              {shownThreads.length ? shownThreads.map((t) => (
                <button key={`${t.user_id}-${t.channel}`} data-testid="message-thread-item" onClick={() => loadThread(t.user_id, t.channel)}
                  className="w-full text-left px-4 py-3 border-b border-border hover:bg-secondary/40 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{t.user_name || "Membre"} <span className="text-[10px] font-normal text-primary">· {CH_LABEL[t.channel]}</span></p>
                    <p className="text-xs text-muted-foreground truncate">{t.last_text}</p>
                  </div>
                  {t.unread > 0 && <span className="min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center shrink-0">{t.unread}</span>}
                </button>
              )) : <p className="p-4 text-sm text-muted-foreground">Aucun message pour le moment.</p>}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {messages.length ? messages.map((m) => {
                  const mine = isStaff ? m.from_role !== "member" : m.from_role === "member";
                  return (
                    <div key={m.id} data-testid="message-item" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"}`}>
                        {!mine && m.from_name && m.from_role !== "member" && <p className="text-[10px] font-bold opacity-70 mb-0.5">{m.from_name}</p>}
                        {m.text}
                        <Attachment m={m} />
                      </div>
                    </div>
                  );
                }) : <p className="text-sm text-muted-foreground text-center mt-6">{isStaff ? "Aucun message." : "Posez votre question au club."}</p>}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="p-2 border-t border-border space-y-2">
                {attach && (
                  <div className="flex items-center justify-between gap-2 rounded-lg bg-secondary px-2.5 py-1.5 text-xs">
                    <span className="truncate flex items-center gap-1"><Paperclip size={12} /> {attach.name}</span>
                    <button type="button" onClick={() => setAttach(null)} className="text-muted-foreground hover:text-destructive"><X size={14} /></button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <label data-testid="message-attach-label" className="shrink-0 w-9 h-9 rounded-full bg-secondary border border-border flex items-center justify-center cursor-pointer hover:bg-secondary/70">
                    <Paperclip size={16} />
                    <input data-testid="message-attach-input" type="file" accept="image/*,application/pdf" className="hidden"
                      onChange={(e) => { doUpload(e.target.files?.[0], setAttach); e.target.value = ""; }} />
                  </label>
                  <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)}
                    placeholder={uploading ? "Chargement du fichier..." : "Votre message..."} className="flex-1 rounded-full bg-secondary border border-border px-4 py-2 text-sm outline-none focus:border-primary" />
                  <button data-testid="message-send-btn" type="submit" disabled={uploading} className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 hover:scale-105 transition-transform disabled:opacity-50"><Send size={16} /></button>
                </div>
              </form>
            </>
          ))}
        </div>
      )}
    </>
  );
}
